import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const empty = () => ({ equipmentId: '', title: '', description: '', priority: 'MEDIUM', plannedDate: '' });

export function MaintenanceOrderModal({ open, onClose, equipment }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => financeApi.createMaintenanceOrder(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'maintenance-orders'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.equipmentId || !form.title.trim()) { setError(t('pm.error.order')); return; }
    save.mutate({ equipmentId: form.equipmentId, title: form.title.trim(), description: form.description || null, priority: form.priority, plannedDate: form.plannedDate || null });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('pm.orderModal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('pm.col.equipment')}</span>
          <select className="input" value={form.equipmentId} onChange={set('equipmentId')} required>
            <option value="">-</option>
            {(equipment || []).map((e) => <option key={e.id} value={e.id}>{e.equipmentNumber} · {e.name}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('pm.col.title')}</span>
          <input className="input" value={form.title} onChange={set('title')} required />
        </label>
        <label className="field">
          <span className="field-label">{t('pm.field.description')}</span>
          <textarea className="input" rows={3} value={form.description} onChange={set('description')} />
        </label>
        <div className="field-row">
          <label className="field" style={{ maxWidth: 180 }}>
            <span className="field-label">{t('pm.col.priority')}</span>
            <select className="input" value={form.priority} onChange={set('priority')}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{t(`pm.priority.${p}`)}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('pm.field.plannedDate')}</span>
            <input className="input" type="date" value={form.plannedDate} onChange={set('plannedDate')} />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
